import type { WSModel } from "@/services/socket/ws.service";
import { type ChatStateThread, useChatStore } from "@/store/chat.store";
import { API } from "@/api/api";
import { WSCommands } from "@/constants/ws";
import { useCache } from "@/composables/useCache";

const { upsertCachedThread } = useCache();

export class ChatHandler implements WSModel {
  private store = useChatStore();
  private api = new API();

  handleWSData(data: any) {
    if (!data.command) {
      return;
    }

    if (data.command === WSCommands.message || data.command === WSCommands.messages) {
      this.transformMessage(data);
    } else if (data.command === WSCommands.thread || data.command === WSCommands.threads) {
      this.transformThread(data);
    }
  }

  async recoveryHandler(lastEvent: Date | null): Promise<void> {
    const timeFrom = lastEvent ? Math.round(lastEvent.getTime() / 1000).toString() : null;
    const resp = await this.api.chat.getThreads(0, 1000, timeFrom);
    if (resp.success && resp.data) {
      for (const thread of resp.data.threads) {
        // @ts-ignore pass different types but it has worked all the time
        this.store.updateThread(thread);
      }

      this.store.sortThreads();
      this.store.recalcThreadsCount();
    }
  }

  onlineStatusHandler(isOnline: boolean): void {
    this.store.setOffline(!isOnline);
  }

  private transformMessage(data: any) {
    if (data.command === "messages" && data.messages) {
      this.store.updateThreadMessage(data.messages);

      return;
    }

    // Handle single message
    const message = { ...data };
    delete message.command;
    this.store.updateThreadMessage(message);
  }

  private async transformThread(data: any) {
    if (data.command === "threads" && data.threads) {
      const threads = data.threads as ChatStateThread[];
      await Promise.all(threads.map((thread) => upsertCachedThread(thread)));

      return;
    }

    // Handle single thread
    const thread = { ...data };
    delete thread.command;
    await upsertCachedThread(thread);

    this.store.sortThreads();
    this.store.recalcThreadsCount();
  }
}
